import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createInstructor, getInstructorById, updateInstructor } from '../../api/instructorApi';
import { Box, Grid, Alert, Fade } from '@mui/material';
import {
  School as SchoolIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { FormPageLayout } from '../../components/templates';
import ModernButton from '../../components/common/ModernButton';
import ModernTextField from '../../components/common/ModernTextField';

const InstructorForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [instructor, setInstructor] = useState({
    name: '',
    description: '',
    email: '',
    course: '',
  });
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (id) {
      const fetchInstructor = async () => {
        try {
          const { data } = await getInstructorById(id);
          setInstructor(data);
        } catch (error) {
          console.error('Failed to fetch instructor:', error);
          showToast('Failed to fetch instructor data', 'error');
        } finally {
          setLoading(false);
        }
      };
      fetchInstructor();
    }
  }, [id, showToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInstructor((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));

    if (name === 'email' && value.trim()) {
      if (!/\S+@\S+\.\S+/.test(value)) {
        setErrors((prev) => ({ ...prev, email: 'Email is invalid' }));
      } else {
        setErrors((prev) => ({ ...prev, email: '' }));
      }
    } else if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!instructor.name.trim()) newErrors.name = 'Name is required';
    if (!instructor.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(instructor.email)) newErrors.email = 'Email is invalid';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, course: true, description: true });

    if (!validateForm()) {
      showToast('Please fix the highlighted errors', 'error');
      return;
    }

    setSaving(true);
    try {
      if (id) {
        await updateInstructor(id, instructor);
        showToast('Instructor updated successfully!', 'success');
      } else {
        await createInstructor(instructor);
        showToast('Instructor created successfully!', 'success');
      }
      navigate('/instructors');
    } catch (error) {
      console.error('Failed to save instructor:', error);
      showToast('Failed to save instructor', 'error');
    } finally {
      setSaving(false);
    }
  };

  const isFormComplete = instructor.name && instructor.email && !errors.name && !errors.email;

  return (
    <FormPageLayout
      title={id ? 'Edit Instructor' : 'Create Instructor'}
      subtitle={id ? 'Update instructor information' : 'Add a new instructor to the system'}
      icon={<SchoolIcon />}
      loading={loading}
      backPath="/instructors"
      backLabel="Back to Instructors"
      actions={
        <ModernButton
          onClick={handleSubmit}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving || !isFormComplete}
        >
          {saving ? 'Saving...' : id ? 'Update Instructor' : 'Create Instructor'}
        </ModernButton>
      }
    >
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ModernTextField
              label="Full Name"
              name="name"
              value={instructor.name}
              onChange={handleChange}
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
              placeholder="Enter instructor's full name"
              required
              fullWidth
              InputProps={{
                endAdornment: touched.name && instructor.name && !errors.name && (
                  <CheckCircleIcon color="success" />
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ModernTextField
              label="Email Address"
              name="email"
              type="email"
              value={instructor.email}
              onChange={handleChange}
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email}
              placeholder="instructor@example.com"
              required
              fullWidth
              InputProps={{
                endAdornment: touched.email && instructor.email && !errors.email && (
                  <CheckCircleIcon color="success" />
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <ModernTextField
              label="Course"
              name="course"
              value={instructor.course}
              onChange={handleChange}
              placeholder="e.g., IELTS Academic, IELTS General Training"
              fullWidth
              InputProps={{
                endAdornment: instructor.course && <CheckCircleIcon color="success" />,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <ModernTextField
              label="Description"
              name="description"
              value={instructor.description}
              onChange={handleChange}
              multiline
              rows={4}
              placeholder="Brief description about the instructor's experience and qualifications..."
              fullWidth
            />
          </Grid>

          {isFormComplete && (
            <Grid item xs={12}>
              <Fade in>
                <Alert severity="success">
                  All required fields completed. Ready to save!
                </Alert>
              </Fade>
            </Grid>
          )}
        </Grid>
      </form>
    </FormPageLayout>
  );
};

export default InstructorForm;
